import React, { useState } from "react";
import { toast } from "react-toastify";
import Button from "../../Button";
import Chip from "../../Chip";
import { ResearchMessage, ResearchPart, ResearchSource, ResearchTable, SOURCE_LABEL } from "../../../types/bd/Research";
import { cellText, downloadFigurePng, downloadTableXlsx, plainColumns, rangeSummary, tableToTsv } from "../../../utils/bd/researchExport";
import ResearchFigure, { figureLabel } from "./ResearchFigure";
import { numberColumns, RangeForm, RangePosition, rangeTitle } from "./RangeCheck";
import ResearchTableEditor from "./ResearchTableEditor";
import classes from "./research.module.scss";

/** Paragraphs, "- " lists and "1. " lists, as the AI writes them. */
export const ResearchText: React.FC<{ text: string }> = ({ text }) => (
    <>
        {String(text).split(/\n{2,}/).map((block, i) => {
            const lines = block.split("\n").filter((l) => l.trim());
            if (lines.length && lines.every((l) => /^\s*[-•*]\s+/.test(l))) return <ul key={i}>{lines.map((l, k) => <li key={k}>{l.replace(/^\s*[-•*]\s+/, "")}</li>)}</ul>;
            if (lines.length && lines.every((l) => /^\s*\d+[.)]\s+/.test(l))) return <ol key={i}>{lines.map((l, k) => <li key={k}>{l.replace(/^\s*\d+[.)]\s+/, "")}</li>)}</ol>;
            return <p key={i}>{lines.map((l, k) => <React.Fragment key={k}>{k > 0 && <br />}{l}</React.Fragment>)}</p>;
        })}
    </>
);

/** The chip that says where a part came from. */
export const SourceChip: React.FC<{ from: ResearchSource }> = ({ from }) => {
    const s = SOURCE_LABEL[from];
    return <Chip tone={s.tone} icon={s.icon} title={s.tooltip}>{s.label}</Chip>;
};

/** One labelled part of an answer; the AI's own knowledge stands apart. */
export const AnswerPart: React.FC<{ part: ResearchPart }> = ({ part }) => (
    <div className={`${classes.part} ${part.from === "general" ? classes.partGeneral : ""}`} role="group" aria-label={SOURCE_LABEL[part.from].label}>
        <SourceChip from={part.from} />
        <ResearchText text={part.text} />
    </div>
);

const isNumberColumn = (t: ResearchTable, i: number) => t.rows.length > 0 && t.rows.every((r) => typeof r[i] === "number" || r[i] === "");

/** A table with Download as Excel and Copy (and any other buttons given); "Edited" when changed by hand. */
export const ResearchTableView: React.FC<{ table: ResearchTable; actions?: React.ReactNode; isEdited?: boolean }> = ({ table, actions, isEdited }) => {
    const plain = plainColumns(table);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(tableToTsv(table));
            toast.success("Table copied. Paste it into Excel, Sheets or an email.");
        } catch {
            toast.error("Copy is blocked in this browser. Download it as Excel instead.");
        }
    };
    return (
        <div className={classes.table}>
            <div className={classes.tableHead}>
                <span className={classes.tableTitle}><i className="bi bi-table" aria-hidden="true" />{table.title}
                    {isEdited && <Chip tone="neutral" icon="pencil" title="Changed by hand on this page. The AI's original table isn't kept.">Edited</Chip>}</span>
                <Button size="xs" variant="ghost" icon="download" onClick={() => downloadTableXlsx(table).catch(() => toast.error("The Excel file couldn't be made."))} title="Download this table as an Excel file">Download as Excel</Button>
                <Button size="xs" variant="ghost" icon="clipboard" onClick={copy} title="Copy the table to paste into Excel, Sheets or an email">Copy</Button>
                {actions}
            </div>
            <div className={classes.tableScroll}>
                <table>
                    <thead><tr>{table.columns.map((c, i) => <th key={i} className={isNumberColumn(table, i) ? classes.num : undefined}>{c}</th>)}</tr></thead>
                    <tbody>{table.rows.map((r, k) => <tr key={k}>{table.columns.map((_c, i) => <td key={i} className={typeof r[i] === "number" ? classes.num : undefined}>{cellText(r[i], plain[i])}</td>)}</tr>)}</tbody>
                </table>
            </div>
        </div>
    );
};

/** The SVG ids of a message's chart, diagram and range check (the PDF finds them by these). */
export const figureId = (messageId: number, which: "chart" | "diagram" | "range") => `research-${which}-${messageId}`;

interface ResearchAnswerProps {
    message: ResearchMessage;
    today: string;
    isCompany: boolean;
    canRemove: boolean;
    onRemove: () => void;
    /** Save a table changed by hand; left out when the person can't edit the page. */
    onTable?: (table: { columns: string[]; rows: Array<Array<string | number>> }) => Promise<boolean>;
    /** Save or clear the answer's range check; left out when the person can't edit the page. */
    onRange?: (range: { column: string; value: string; label: string } | null) => Promise<boolean>;
}

/** A question and its answer on a research page. */
const ResearchAnswer: React.FC<ResearchAnswerProps> = ({ message, today, isCompany, canRemove, onRemove, onTable, onRange }) => {
    const { answer } = message;
    const [isRangeOpen, setIsRangeOpen] = useState(false);
    const [isEditingTable, setIsEditingTable] = useState(false);
    const canRange = !!onRange && !!answer.table && numberColumns(answer.table).length > 0;
    const png = async (which: "chart" | "diagram" | "range", title: string) => {
        if (!(await downloadFigurePng(figureId(message.researchMessageId, which), title))) toast.error("The picture couldn't be made in this browser.");
    };
    return (
        <>
            <div className={classes.question}>{message.question}</div>
            {message.createdByName && <div className={classes.questionMeta}>{message.createdByName}</div>}
            <article className={classes.answer}>
                <div className={classes.answerHead}>
                    <span className={classes.answerMark}><i className="bi bi-stars" aria-hidden="true" />CRX</span>
                    {message.masked && <Chip tone="neutral" icon="shield-lock" title="What was hidden before the question reached the AI. CRX put the real values back in the answer.">{message.masked}</Chip>}
                    {isCompany && <Chip tone={message.nameShown ? "warning" : "neutral"} icon={message.nameShown ? "eye" : "eye-slash"} title={message.nameShown ? "The company's name went to the AI as written for this answer." : "The company's name was hidden from the AI (Org-A) for this answer."}>{message.nameShown ? "Name shown" : "Name hidden"}</Chip>}
                    {canRemove && <span className={classes.answerHeadEnd}><Button size="xs" variant="ghost" icon="x-lg" onClick={onRemove} title="Remove this question and answer from the page and its PDF">Remove</Button></span>}
                </div>
                {answer.parts.map((p, i) => <AnswerPart key={i} part={p} />)}
                {answer.table && isEditingTable && onTable && <ResearchTableEditor table={answer.table} onSave={onTable} onCancel={() => setIsEditingTable(false)} />}
                {answer.table && !isEditingTable && <ResearchTableView table={answer.table} isEdited={answer.tableEdited} actions={<>
                    {onTable && <Button size="xs" variant="ghost" icon="pencil-square" onClick={() => { setIsEditingTable(true); setIsRangeOpen(false); }} title="Fix a number, add a row you know, or drop rows (for example ones with no source)">Edit table</Button>}
                    {canRange && !answer.range && !isRangeOpen && <Button size="xs" variant="ghost" icon="rulers" onClick={() => setIsRangeOpen(true)} title="Place one figure, such as the owner's pay, among a column's numbers: lowest, quartiles, median, highest. CRX works it out, not the AI.">Range check</Button>}
                </>} />}
                {isRangeOpen && answer.table && onRange && <RangeForm idKey={message.researchMessageId} table={answer.table} range={answer.range} onSave={onRange} onCancel={() => setIsRangeOpen(false)} />}
                {answer.range && (
                    <div className={classes.range}>
                        <ResearchFigure spec={{ type: "range", title: rangeTitle(answer.range), range: answer.range }} svgId={figureId(message.researchMessageId, "range")} onDownload={() => png("range", rangeTitle(answer.range!))} />
                        <p className={classes.rangeSummary}><RangePosition range={answer.range} /><span>{rangeSummary(answer.range)}</span></p>
                        {onRange && !isRangeOpen && (
                            <div className={classes.rangeActions}>
                                <Button size="xs" variant="ghost" icon="pencil" onClick={() => setIsRangeOpen(true)} title="Change the column or the figure">Change</Button>
                                <Button size="xs" variant="ghost" icon="x-lg" onClick={() => onRange(null)} title="Take the range check off this answer and its PDF">Remove range check</Button>
                            </div>
                        )}
                    </div>
                )}
                {answer.chart && <ResearchFigure spec={answer.chart} svgId={figureId(message.researchMessageId, "chart")} today={today} onDownload={() => png("chart", answer.chart!.title || figureLabel(answer.chart!))} />}
                {answer.diagram && <ResearchFigure spec={answer.diagram} svgId={figureId(message.researchMessageId, "diagram")} today={today} onDownload={() => png("diagram", answer.diagram!.title || figureLabel(answer.diagram!))} />}
            </article>
        </>
    );
};

export default ResearchAnswer;
